"use client";

import { useEffect } from "react";
import Image from "next/image";
import { Box, Tooltip } from "@mui/material";
import { FONT_UI } from "@/src/components/common/broadsheet/tokens";

const CROSSMARK_CONFIG = {
  VERIFICATION:
    "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpYXQiOjE2NjY1MDE5NDIsInZlciI6IjIuMi4yIn0.Y0BGMBQd81otZafJBR85JPGLuLaEwoctmNA-p3l1euw",
  ENDPOINT: "https://crossmark.crossref.org/dialog",
  SCRIPT_VERSION: "2.2.2",
  STYLESHEET_URL: "https://crossmark-cdn.crossref.org/widget/v2.0/style.css",
  LOGO_URL: "https://crossmark-cdn.crossref.org/images/logo-crossmark.svg",
};

const isIOS = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

const ArticleCrossmark = ({ doi }: { doi: string }) => {
  useEffect(() => {
    const link = document.createElement("link");
    link.href = CROSSMARK_CONFIG.STYLESHEET_URL;
    link.type = "text/css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, []);

  const handleShow = () => {
    const existingOverlay = document.querySelector(".crossmark-overlay");
    if (existingOverlay) {
      existingOverlay.parentNode?.removeChild(existingOverlay);
    }

    const params = {
      doi: doi,
      domain: window.location.hostname,
      uri_scheme: window.location.protocol,
      cm_version: CROSSMARK_CONFIG.SCRIPT_VERSION,
      verification: CROSSMARK_CONFIG.VERIFICATION,
    };

    const queryString = Object.entries(params)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`,
      )
      .join("&");

    const container = document.createElement("div");
    container.id = "crossmark-widget";
    container.innerHTML = `
      <div class="crossmark-reset crossmark-overlay"></div>
      <div class="crossmark-reset crossmark-popup">
        <div class="crossmark-reset crossmark-popup__offset ${isIOS() ? "is-ios" : ""}">
          <div class="crossmark-reset crossmark-popup__inner">
            <div class="crossmark-reset crossmark-popup__header">
              <a target="_blank" href="http://www.crossref.org/crossmark">
                <img class="crossmark-reset crossmark-popup__logo" src="${CROSSMARK_CONFIG.LOGO_URL}">
              </a>
              <button class="crossmark-reset crossmark-popup__btn-close"></button>
            </div>
            <div class="crossmark-reset crossmark-popup__content-wrapper">
              <iframe class="crossmark-reset crossmark-popup__content" src="${CROSSMARK_CONFIG.ENDPOINT}?${queryString}"></iframe>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(container);

    // Add event listeners
    const overlay = container.querySelector(".crossmark-overlay");
    const popup = container.querySelector(".crossmark-popup");
    const closeBtn = container.querySelector(".crossmark-popup__btn-close");
    const inner: any = container.querySelector(".crossmark-popup__inner");

    const closeDialog = (event: any) => {
      container.style.display = "none";
      event.preventDefault();
      event.stopPropagation();
    };

    [overlay, popup, closeBtn].forEach((element) => {
      element?.addEventListener("click", closeDialog);
    });

    inner?.addEventListener("click", (event: any) => {
      event.stopPropagation();
    });

    container.style.display = "block";

    if (isIOS() && inner) {
      inner.style.top = `${window.scrollY}px`;
    }
  };

  return (
    <Tooltip title={<strong>Crossmark</strong>} arrow placement="bottom">
      <Box
        component="button"
        type="button"
        onClick={handleShow}
        aria-label="Crossmark: check for updates"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          flex: "none",
          border: 0,
          p: 0,
          background: "none",
          cursor: "pointer",
          fontFamily: FONT_UI,
          fontSize: "0.6875rem",
          lineHeight: 1.2,
          color: "custom.inkMuted",
          "&:hover": { color: "custom.link" },
        }}
      >
        <Image
          src="https://crossmark-cdn.crossref.org/widget/v2.0/logos/CROSSMARK_Color_square_no_text.svg"
          alt=""
          width={18}
          height={18}
          quality={100}
          style={{ width: 18, height: 18 }}
        />
        Crossmark
      </Box>
    </Tooltip>
  );
};

export default ArticleCrossmark;
